/**
 * CX-G07 / S6 — iEEG electrode localization + specialty montage helpers.
 * Pure functions: contact parsing, neighbor bipolar generation, trend grouping.
 */

export const IEEG_LOCALIZE_ALGORITHM_ID = "ieeg-localize-v1.0.0";
export const IEEG_MODEL_CARD_URI = "qms://model-card/ieeg-localize-v1";

export type IeegContactKind = "depth" | "grid" | "strip" | "unknown";

export type IeegContact = {
  name: string;
  x: number;
  y: number;
  z: number;
  kind: IeegContactKind;
  shaft?: string | null;
  indexOnShaft?: number | null;
};

export type IeegMontagePair = {
  label: string;
  active: string;
  reference: string;
};

export type IeegMontageMode = "longitudinal" | "neighbor" | "grid_row";

export type IeegTrendGroup = {
  groupId: string;
  label: string;
  contacts: string[];
};

const DEPTH_RE = /^([A-Za-z]+(?:'[A-Za-z]+)?)(\d+)$/;
const GRID_RE = /^([A-Za-z]+)(\d+)[_-]?(\d+)$/;

export function classifyIeegContactName(name: string): {
  kind: IeegContactKind;
  shaft: string | null;
  indexOnShaft: number | null;
} {
  const trimmed = String(name || "").trim();
  const grid = GRID_RE.exec(trimmed);
  if (grid) {
    return {
      kind: "grid",
      shaft: `${grid[1]}${grid[2]}`,
      indexOnShaft: Number(grid[3]),
    };
  }
  const depth = DEPTH_RE.exec(trimmed);
  if (depth) {
    return {
      kind: "depth",
      shaft: depth[1],
      indexOnShaft: Number(depth[2]),
    };
  }
  if (/grid|strip|seeg|depth|ieeg/i.test(trimmed)) {
    return { kind: "strip", shaft: trimmed.replace(/\d+$/, "") || trimmed, indexOnShaft: null };
  }
  return { kind: "unknown", shaft: null, indexOnShaft: null };
}

export function parseIeegContacts(
  rows: Array<{ name: string; x: number; y: number; z: number }>,
): IeegContact[] {
  return rows
    .filter(
      (row) =>
        row.name &&
        Number.isFinite(row.x) &&
        Number.isFinite(row.y) &&
        Number.isFinite(row.z),
    )
    .map((row) => {
      const meta = classifyIeegContactName(row.name);
      return {
        name: row.name,
        x: row.x,
        y: row.y,
        z: row.z,
        kind: meta.kind,
        shaft: meta.shaft,
        indexOnShaft: meta.indexOnShaft,
      };
    });
}

/** Neighbor bipolar along each shaft (A1-A2, A2-A3, …). */
export function generateIeegNeighborMontage(
  contacts: IeegContact[],
  mode: IeegMontageMode = "longitudinal",
): IeegMontagePair[] {
  const byShaft = new Map<string, IeegContact[]>();
  for (const contact of contacts) {
    const shaft = contact.shaft || contact.name;
    const list = byShaft.get(shaft) || [];
    list.push(contact);
    byShaft.set(shaft, list);
  }

  const pairs: IeegMontagePair[] = [];
  for (const [shaft, list] of byShaft) {
    const ordered = [...list].sort(
      (a, b) => (a.indexOnShaft ?? 0) - (b.indexOnShaft ?? 0),
    );
    if (mode === "grid_row" && ordered.length < 2) continue;
    for (let i = 0; i < ordered.length - 1; i += 1) {
      const active = ordered[i];
      const reference = ordered[i + 1];
      pairs.push({
        label: `${active.name}-${reference.name}`,
        active: active.name,
        reference: reference.name,
      });
    }
    if (mode === "neighbor" && ordered.length >= 3) {
      // skip-one neighbor for denser SEEG review
      for (let i = 0; i < ordered.length - 2; i += 1) {
        const active = ordered[i];
        const reference = ordered[i + 2];
        pairs.push({
          label: `${active.name}-${reference.name}`,
          active: active.name,
          reference: reference.name,
        });
      }
    }
    void shaft;
  }
  return pairs;
}

export function groupIeegContactsForTrends(
  contacts: IeegContact[],
): IeegTrendGroup[] {
  const byShaft = new Map<string, string[]>();
  for (const contact of contacts) {
    const key = contact.shaft || "ungrouped";
    const list = byShaft.get(key) || [];
    list.push(contact.name);
    byShaft.set(key, list);
  }
  return Array.from(byShaft.entries()).map(([groupId, names]) => ({
    groupId,
    label: `iEEG ${groupId}`,
    contacts: names,
  }));
}

export function serializeIeegContactsCsv(contacts: IeegContact[]): string {
  const header = "name,x,y,z,kind,shaft,index";
  const lines = contacts.map(
    (c) =>
      `${c.name},${c.x},${c.y},${c.z},${c.kind},${c.shaft ?? ""},${c.indexOnShaft ?? ""}`,
  );
  return [header, ...lines].join("\n");
}
